import { env } from "./env";

/** URL of a stored image. Cookie-authenticated; the server scopes it to the owner. */
export function assetUrl(assetId: string): string {
	return `${env.VITE_SERVER_URL}/assets/${encodeURIComponent(assetId)}`;
}

const DATA_URL_SEPARATOR = ",";

export function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = () => reject(reader.error);
		reader.onload = () => {
			const { result } = reader;
			if (typeof result !== "string") {
				reject(new Error("Unexpected FileReader result"));
				return;
			}
			resolve(result.slice(result.indexOf(DATA_URL_SEPARATOR) + 1));
		};
		reader.readAsDataURL(blob);
	});
}

/** Re-encode any browser-decodable image as PNG. */
async function toPngBlob(blob: Blob): Promise<Blob> {
	const bitmap = await createImageBitmap(blob);
	try {
		const canvas = document.createElement("canvas");
		canvas.width = bitmap.width;
		canvas.height = bitmap.height;
		canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
		const png = await new Promise<Blob | null>((resolve) =>
			canvas.toBlob(resolve, "image/png")
		);
		if (!png) {
			throw new Error("Could not convert the image to PNG");
		}
		return png;
	} finally {
		bitmap.close();
	}
}

/**
 * Download a stored asset (with the session cookie) as base64 PNG. Uploads may
 * be JPEG or WebP; they're converted, since Codex gets the bytes as a .png.
 */
export async function fetchAssetBase64(assetId: string): Promise<string> {
	const response = await fetch(assetUrl(assetId), { credentials: "include" });
	if (!response.ok) {
		throw new Error(`Failed to load image (${response.status})`);
	}
	const blob = await response.blob();
	return blobToBase64(blob.type === "image/png" ? blob : await toPngBlob(blob));
}
